import './App.css';
import { useState, useEffect } from 'react';
import { appData } from './data/data.js';
import {Routes, Route, useNavigate } from 'react-router-dom';
import Button from './button.js';
import BackButton from './back-button.js';
import Question from './question.js';
import ExternalTextLink from './external-text-link.js';
import Modal from './modal.js';
import Differentials from './differentials.js';
import PayCodeStub from './pay-code-stub.js';
import ListOfCodes from './list-of-codes.js';
import ContentBox from './content-box.js';
import StepCounter from './step-counter.js';
import Dropdown from './dropdown.js';
import Checkbox from './checkbox.js';
import EquateDate from './equate-date.js';
import AppointedDate from './appointed-date.js';
import LongevityDate from './longevity-date.js';
import SalarySchedule from './salary-schedule.js';
import SalaryScheduleCodes from './salary-schedule-codes.js';
import LongevitySchedule from './longevity-schedule.js';
import Alert from './alert.js';
import SalaryCalculator from './salary-calculator.js';

/**
 * Main component for the salary calculator application.
 * This component manages the overall state and routing of the application,
 * renders different components based on the current question, and handles
 * user interactions such as navigation and form inputs.
 *
 * @returns {JSX.Element} The rendered Main component
 */

export default function Main() {
  // Current Date
  const currentDate = new Date();

  // Set router navigation
  const navigate = useNavigate()
  let pathName = window.location.pathname;

  // State variables for managing the application's state
  let [index, setIndex] = useState("home");
  let [jobTitle, setJobTitle] = useState("default");
  let [stepNumber, setStepNumber] = useState(1);
  let [questionPath, setQuestionPath] = useState([]);
  let [animation, setAnimation] = useState(false);
  let [showNextQestion, setShowNextQuestion] = useState(false);
  let [showNextPayCode, setShowNextPayCode] = useState(false);
  let [active, setActive] = useState();
  let [activePayCode, setActivePayCode] = useState();
  let [selectedValue, setSelectedValue] = useState();
  let [selectedPayCode, setSelectedPayCode] = useState();
  let [isModalActive, setIsModalActive] = useState(false);
  let [dropdownValues, setDropdownValues] = useState({});
  let [checkboxValues, setCheckboxValues] = useState({});
  let [buttonVar, setButtonVar] = useState({});
  let [payCodeVar, setPayCodeVar] = useState();
  let [calculatedPayCodeVar, setCalculatedPayCodeVar] = useState();
  let [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  let [stepCode, setStepCode] = useState();
  let [stepCodeCurrent, setStepCodeCurrent] = useState();
  let [pastExperience, setPastExperience] = useState();
  let [longevity, setLongevity] = useState();
  let [currentToAppointedDifference, setCurrentToAppointedDifference] = useState();

  // Filter the current question based on the index/jobTitle
  const currentQuestion = appData.filter(data => 
    data.id === index && data.jobTitle === jobTitle
  );

  // Background Styles
  let backgroundStyle = {};
  if(currentQuestion[0].background){
    backgroundStyle = {
      backgroundColor: currentQuestion[0].background[0].color,
      backgroundImage: `url(${currentQuestion[0].background[0].image})`,
      backgroundPosition: currentQuestion[0].background[0].position,
      backgroundSize: currentQuestion[0].background[0].size,
      backgroundRepeat: currentQuestion[0].background[0].repeat,
      width: currentQuestion[0].background[0].width,
      height: currentQuestion[0].background[0].height,
    }
  }

  // Function to render components based on component "Type" in data.js
  const renderComponents = (data) => {
  return data.components.map((component, index) => {
    switch(component.type) {
      case 'Image':
        return <img key={`image-${index}`} className="image" src={component.content} alt={component.alt} width={component.width} />;
      case 'Title':
        return <h1 key={`title-${index}`} className={component.underline ? 'underline title': 'title'}>{component.content}</h1>;
      case 'Sub Title':
        return <h2 key={`subtitle-${index}`} className="sub-title" dangerouslySetInnerHTML={{__html: component.content}}></h2>;
      case 'Content':
        return <p key={`content-${index}`} className="content" style={{textAlign: component.textAlign}} dangerouslySetInnerHTML={{__html: component.content}}></p>;
      case 'Content Box':
        return (
          <ContentBox key={`content-box-${index}`} title={component.title} subTitle={component.subTitle}  textAlign={component.textAlign} icon={component.icon} errorCheck={component.errorCheck} currentQuestion={component.content} stepStatus={buttonVar} modal={component.modal} isModalActive={isModalActive} setModalIsActive={setIsModalActive} stepCode={stepCodeCurrent} differential={payCodeVar} calculatedPayCode={calculatedPayCodeVar} jobTitle={dropdownValues['job-title']} hoursPerWeek={dropdownValues['hours-per-week']}/>
        );
      case 'Question':
        return (
          <Question key={`question-${index}`} currentQuestion={component.content} currentId={currentQuestion[0].id} handleNextQuestion={handleNextQuestion} handleShowNextQuestion={setShowNextQuestion} showNextQestion={showNextQestion} handleSetActive={setActive} active={active} handleSetSelectedValue={setSelectedValue} selectedValue={selectedValue} id={component.id} value={setButtonVar[component.id] || ''} currentStep={currentQuestion[0].stepNumber} longevity={longevity}/>
        );
      case 'Button':
        return (
          <Button key={`button-${index}`}  label={component.label} linkId={component.linkId} size={component.size} currentQuestion={component.content} currentId={currentQuestion[0].id} handleNextQuestion={handleNextQuestion} id={component.id} value={setButtonVar[component.id] || ''} dropdownVar={dropdownValues} appointedDate={dropdownValues['appointed-date']} updateDropdownVar={updateDropdownVar} checkboxVar={checkboxValues} isCheckboxChecked={isCheckboxChecked} precedence={component.precedence} currentStep={currentQuestion[0].stepNumber} longevity={longevity} jobTitle={dropdownValues['job-title']}/>
        );
      case 'External Text Link':
        return (
          <ExternalTextLink key={`external-link-${index}`} label={component.label} link={component.link} isButtonStyle={component.buttonStyle} />
        );
      case 'Modal':
        return (
          <Modal key={`modal-${index}`} currentQuestion={component.content} label={component.label} title={component.title} isActive={isModalActive} setIsActive={setIsModalActive}/>
        );
      case 'Differentials':
        return (
          <Differentials key={`differential-${index}`} currentQuestion={component.content} jobTitle={dropdownValues['job-title']} hoursPerWeek={dropdownValues['hours-per-week']}/>
        );
      case 'Pay Code Stub':
        return (
          <PayCodeStub key={`pay-code-${index}`} paycode={component.paycode} title={component.title} content={component.content} handelPayCodeVar={setPayCodeVar} dropdownVars={dropdownValues} handleStepCode={setStepCode} handlePastExperience={setPastExperience} handleStepCodeCurrent={setStepCodeCurrent} handleCalculatedPayCodeVar={setCalculatedPayCodeVar} currentStep={currentQuestion[0].stepNumber} hasError={component.error} jobTitle={dropdownValues['job-title']} hoursPerWeek={dropdownValues['hours-per-week']} paraprofessionalCredits={payCodeVar} handleCurrentToAppointedDifference={setCurrentToAppointedDifference}/>
        );
      case 'List Of Codes':
        return (
          <ListOfCodes key={`list-of-codes-${index}`} currentQuestion={component.content} currentId={currentQuestion[0].id} handleNextQuestion={handleNextQuestion} handleShowNextQuestion={setShowNextPayCode} showNextQestion={showNextPayCode} handleSetActive={setActivePayCode} active={activePayCode} handleSetSelectedValue={setSelectedPayCode} selectedValue={selectedPayCode} jobTitle={dropdownValues['job-title']} hoursPerWeek={dropdownValues['hours-per-week']}/>
        );
      case 'Dropdown':
        return (
          <Dropdown key={`dropdown-${index}`} id={component.id} currentQuestion={component.content} value={dropdownValues[component.id] || ''} onChange={handleDropdownChange}/>
        );
      case 'Checkbox':
        return (
          <Checkbox key={`checkbox-${index}`} id={component.id} currentQuestion={component.content} value={checkboxValues[component.id] || {}}  onChange={handleCheckboxChange} isCheckboxChecked={isCheckboxChecked}/>
        );
      case 'Equate Date':
        return (
          <EquateDate key={`equate-date-${index}`} content={component.content} equateDate={dropdownValues['equate-date']} appointedDate={dropdownValues['appointed-date']} termLength={dropdownValues['term-length']}/>
        );
      case 'Salary Schedule':
        return (
          <SalarySchedule key={`salary-schedule-${index}`} title={component.title} content={component.content} stepCode={stepCode} appointedDate={dropdownValues['appointed-date']} equateDate={dropdownValues['equate-date']} termLength={dropdownValues['term-length']} jobTitle={dropdownValues['job-title']} terms={pastExperience} includeLongevity={component.longevity} stepStatus={buttonVar} currentStep={currentQuestion[0].stepNumber}/>
        );
      case 'Salary Schedule Codes':
        return (
          <SalaryScheduleCodes key={`salary-schedule-codes-${index}`} currentQuestion={component.content} title={component.title} stepCode={stepCodeCurrent} differential={payCodeVar} stepStatus={buttonVar} jobTitle={dropdownValues['job-title']} adultEd={buttonVar['adult-education-program']} currentToAppointedDifference={currentToAppointedDifference} hoursPerWeek={dropdownValues['hours-per-week']} modal={component.modal} isModalActive={isModalActive} setModalIsActive={setIsModalActive}/>
        );
      case 'Alert':
        return (
          <Alert key={`alert-${index}`} content={component.content} level={component.level}/>
        );
      case 'Appointed Date':
        return (
          <AppointedDate key={`appointed-date-${index}`} content={component.content} appointedDate={dropdownValues['appointed-date']}/>
        );
      case 'Longevity Date':
        return (
          <LongevityDate key={`longevity-date-${index}`} appointedDate={dropdownValues['appointed-date']} longevity={longevity} jobTitle={dropdownValues['job-title']}/>
        );
      case 'Longevity Schedule':
        return (
          <LongevitySchedule key={`longevity-schedule-${index}`} appointedDate={dropdownValues['appointed-date']} longevity={longevity} jobTitle={dropdownValues['job-title']} title={component.title} currentStep={currentQuestion[0].stepNumber} stepStatus={buttonVar}/>
        );
      case 'Salary Calculator':
        return (
          <SalaryCalculator key={`longevity-schedule-${index}`}/>
        );
      default:
        return null;
    }
  });
};

  // Function to handle user navigation to the next question
  function handleNextQuestion(event, id, value) {
    let nextIndex;
    nextIndex = event.currentTarget.value;
    setQuestionPath([...questionPath, event.currentTarget.getAttribute('data-current')]);

    // Sets vars based on button clicks
    setButtonVar(prevValues => ({
      ...prevValues,
      [id]: value
    }));
    
    setAnimation(true);
    setSelectedValue(null);
    setActive(null);
    setActivePayCode(null);
    setTimeout(() => {
        if (dropdownValues['job-title']) {
          setJobTitle(dropdownValues['job-title']);
        }
        setIndex(nextIndex);
        navigate(nextIndex);
        setAnimation(false);
        setShowNextQuestion(false);
        setShowNextPayCode(false);
    }, 400);
  }

  // Function to handle user navigation to the previous question
  function handleBack(event) {
    let prePage = questionPath.pop();

    // Get the current step number
    const currentStepNumber = currentQuestion[0].stepNumber;

    // Clear the buttonVar for the current step only
    setButtonVar(prevValues => {
      const newValues = { ...prevValues };
      // Remove all keys that start with the current step number
      Object.keys(newValues).forEach(key => {
        if (key.startsWith(`step-${currentStepNumber}-status`)) {
          delete newValues[key];
        }
      });
      return newValues;
    });

    // Remove job-title from dropdownValues when going back to home or job-title page
    if (prePage === 'home' || prePage === 'job-title') {
      setDropdownValues(prevValues => {
        const { 'job-title': _, ...rest } = prevValues;
        return rest;
      });
    }

    // Remove equate-date related keys if navigating back from the equate date question
    if (currentQuestion[0].id == 'what-is-your-equate-date') {
      setDropdownValues(prevValues => {
        const { 'equate-date': _, 'equate-day': __, 'equate-month': ___, 'equate-year': ____, ...rest } = prevValues;
        return rest;
      });
    }

    // Remove term-length related keys if navigating back from the term length question
    if (currentQuestion[0].id == 'how-much-salary-step-credit') {
      setDropdownValues(prevValues => {
        const { 'term-length': _, ...rest } = prevValues;
        return rest;
      });
    }

    setAnimation(true);
    setSelectedValue(null);
    setActive(null);
    setActivePayCode(null);
    setTimeout(() => {
        setIndex(prePage);
        if(prePage == "home"){
          navigate("/");
          setJobTitle("default"); // Reset Job Title
        }
        else{
          if(prePage == "job-title"){
            setJobTitle("default"); // Reset Job Title
          }
          navigate(prePage);
        }
        setAnimation(false);
        setShowNextQuestion(false);
        setShowNextPayCode(false);
    }, 400);
  }

  // Functions to handle dropdown selections
  useEffect(() => {
    const { 'appointed-day': day, 'appointed-month': month, 'appointed-year': year } = dropdownValues;
    // Combines dropdown values into a single object
    if (day && month && year) {
      const dateString = `${month.padStart(2, '0')}/${day}/${year}`;
      setDropdownValues(prev => ({
        ...prev,
        'appointed-date': dateString
      }));
    }
  }, [dropdownValues['appointed-day'], dropdownValues['appointed-month'], dropdownValues['appointed-year']]);

  useEffect(() => {
    const { 'equate-day': day, 'equate-month': month, 'equate-year': year } = dropdownValues;
    // Combines dropdown values into a single object
    if (day && month && year) {
      const dateString = `${month.padStart(2, '0')}/${day}/${year}`;
      setDropdownValues(prev => ({
        ...prev,
        'equate-date': dateString
      }));
    }

    // Sets Longevity based off appointed date
    let appointedDate = new Date(dropdownValues['appointed-date']);
    let longevityLength = (currentDate.getTime() - appointedDate.getTime()) / 31556926000;
    setLongevity(longevityLength);
    
  }, [dropdownValues['equate-day'], dropdownValues['equate-month'], dropdownValues['equate-year'], dropdownValues['appointed-date']]);

  useEffect(() => {
    // Set default values for differentials based on Job Title
    if (dropdownValues['job-title']) {
      if (dropdownValues['job-title'] === 'teacher' || dropdownValues['job-title'] === 'adult-education-teacher') {
        setPayCodeVar("MA");
        setCalculatedPayCodeVar("MA");
      }
      if (dropdownValues['job-title'] === 'school-secretary') {
        setPayCodeVar("WA");
        setCalculatedPayCodeVar("WA");
      }
      if (dropdownValues['job-title'] === 'guidance-counselor') {
        setPayCodeVar("WF");
        setCalculatedPayCodeVar("WF");
      }
      if (dropdownValues['job-title'] === 'lab-specialist') {
        setPayCodeVar("WT");
        setCalculatedPayCodeVar("WT");
      }
      if (dropdownValues['job-title'] === 'school-psychologist' || dropdownValues['job-title'] === 'social-worker') {
        setPayCodeVar("WK");
        setCalculatedPayCodeVar("WK");
      }
    }
  }, [dropdownValues['job-title']]);

  useEffect(() => {
    // If the user types in a specfic URL and tries to skip the procress, navigate to the home screen
    if(currentQuestion[0].id != pathName){
      navigate("/");
    }
  }, []);

  // Function to handles dropdown change
  function handleDropdownChange(id, value) {
    setDropdownValues(prevValues => ({
        ...prevValues,
        [id]: value
    }));
}

  // Function to handle checkbox selections
  function handleCheckboxChange(id, name, checked) {
    setCheckboxValues(prevValues => ({
        ...prevValues,
        [id]: {
            ...prevValues[id],
            [name]: checked
        }
    }));
    setIsCheckboxChecked(checked);
  }

  // Function to update dropdown variable 
  const updateDropdownVar = (newDropdownVar) => {
    setDropdownValues(newDropdownVar);
  };

  return (
    <>
        <section className={animation ? 'animateOut main': 'main'} style={backgroundStyle}>
            <div className="inner-main-wrapper">
              {currentQuestion[0].id != "home" && <BackButton handleBack={handleBack} />}
                <div className={currentQuestion[0].id}>
                    <Routes>
                        <Route exact path={currentQuestion[0].id == "home" ? '/' : currentQuestion[0].id} element={
                            <>
                                {renderComponents(currentQuestion[0])}
                            </>
                        } />
                    </Routes>
                </div>
            </div>
            {currentQuestion[0].stepNumber && !isModalActive && (
              <StepCounter currentStep={currentQuestion[0].stepNumber} stepStatus={buttonVar} jobTitle={dropdownValues['job-title']}/>
            )}
        </section>
    </>
  );
}